import React, { useState } from 'react';
import { Button, Alert, Container, Row, Col, Card, Form } from 'react-bootstrap';

const SeamlessLoginDemo = () => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState('demo-user@example.com');
  const [purchaseComplete, setPurchaseComplete] = useState(false);

  const simulatePurchaseFlow = async () => {
    setLoading(true);
    setError(null);
    setStep(1);
    
    try {
      // Step 1: Simulate user making a purchase
      console.log('🛒 [Demo] Step 1: User completes purchase...');
      setStep(1);
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Step 2: Create user account (happens in Stripe webhook)
      console.log('👤 [Demo] Step 2: Creating user account...');
      setStep(2);
      const createUserResponse = await fetch(`${process.env.REACT_APP_API_URL}/users/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email,
          firstName: 'Demo',
          lastName: 'User'
        })
      });

      if (createUserResponse.ok) {
        const userData = await createUserResponse.json();
        console.log('✅ [Demo] User account created:', userData);
      } else {
        console.log('ℹ️ [Demo] User may already exist');
      }
      
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Step 3: Use real checkout for Stripe proof
      console.log('🔐 [Demo] Step 3: Real checkout required for auto-login token...');
      setStep(3);

      setResult({
        method: 'stripe_checkout_required',
        userEmail: email,
        redirectTo: '/checkout/starter-plan',
        message: 'Post-purchase auto-login requires Stripe payment or setup intent proof. Use real checkout to generate it.',
      });
      setStep(4);
      setPurchaseComplete(true);
      
    } catch (err) {
      console.error('❌ [Demo] Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const performSeamlessLogin = async () => {
    if (result?.redirectTo) {
      window.location.href = result.redirectTo;
    }
  };

  const resetDemo = () => {
    setStep(1);
    setResult(null);
    setError(null);
    setPurchaseComplete(false);
    setLoading(false);
  };

  const getStepStatus = (stepNum) => {
    if (step > stepNum) return '✅';
    if (step === stepNum && loading) return '🔄';
    if (step === stepNum) return '👉';
    return '⏳';
  };

  return (
    <Container className="mt-5">
      <Row className="justify-content-center">
        <Col md={10}>
          <Card>
            <Card.Header className="bg-primary text-white">
              <h3 className="mb-0">🚀 Seamless Auto-Login Demo</h3>
              <p className="mb-0 mt-2">
                Complete demonstration of the seamless login flow after purchase
              </p>
            </Card.Header>
            <Card.Body>
              
              {/* Email Input */}
              <Form.Group className="mb-4">
                <Form.Label><strong>Demo Email Address:</strong></Form.Label>
                <Form.Control
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter demo email"
                  disabled={loading || purchaseComplete}
                />
              </Form.Group>

              {/* Demo Steps */}
              <div className="mb-4">
                <h5>Demo Process:</h5>
                <ul className="list-unstyled">
                  <li className="mb-2">
                    <span className="me-2">{getStepStatus(1)}</span>
                    <strong>Step 1:</strong> User completes purchase on Stripe
                  </li>
                  <li className="mb-2">
                    <span className="me-2">{getStepStatus(2)}</span>
                    <strong>Step 2:</strong> Stripe webhook creates user account
                  </li>
                  <li className="mb-2">
                    <span className="me-2">{getStepStatus(3)}</span>
                    <strong>Step 3:</strong> Continue through real Stripe checkout
                  </li>
                  <li className="mb-2">
                    <span className="me-2">{getStepStatus(4)}</span>
                    <strong>Step 4:</strong> Checkout returns with payment proof
                  </li>
                </ul>
              </div>

              {/* Controls */}
              <div className="mb-4">
                {!purchaseComplete && (
                  <Button 
                    variant="primary" 
                    size="lg"
                    onClick={simulatePurchaseFlow}
                    disabled={!email || loading}
                  >
                    {loading ? '🔄 Processing...' : '🛒 Simulate Purchase Flow'}
                  </Button>
                )}
                
                {purchaseComplete && result && (
                  <div>
                    <Button 
                      variant="success" 
                      size="lg"
                      onClick={performSeamlessLogin}
                      className="me-3"
                    >
                      Continue to Checkout
                    </Button>
                    <Button 
                      variant="outline-secondary"
                      onClick={resetDemo}
                    >
                      🔄 Reset Demo
                    </Button>
                  </div>
                )}
              </div>

              {/* Error Display */}
              {error && (
                <Alert variant="danger">
                  <strong>❌ Error:</strong> {error}
                </Alert>
              )}

              {/* Results Display */}
              {result && (
                <Alert variant="info">
                  <h5>Real Checkout Required</h5>
                  <p>Auto-login tokens are only issued after Stripe returns verified payment proof.</p>
                  <ul>
                    <li><strong>Method:</strong> {result.method}</li>
                    <li><strong>Email:</strong> {result.userEmail}</li>
                  </ul>
                  
                  <div className="mt-3 p-3 bg-light rounded">
                    <h6>What happens in checkout:</h6>
                    <ol>
                      <li>Stripe confirms a payment or setup intent</li>
                      <li>The frontend requests post-purchase auto-login with that Stripe proof</li>
                      <li>The navigation URL stays tokenless</li>
                      <li>The frontend validates the response-body token before redirect</li>
                    </ol>
                    
                    <div className="mt-3">
                      <strong>Redirect URL:</strong>
                      <div className="bg-white p-2 border rounded mt-2" style={{ fontSize: '0.85em', wordBreak: 'break-all' }}>
                        {result.redirectTo || result.magicLink}
                      </div>
                    </div>
                  </div>
                </Alert>
              )}

              {/* Additional Info */}
              <div className="mt-4 p-3 bg-info bg-opacity-10 border border-info rounded">
                <h6>ℹ️ About This Demo</h6>
                <p className="mb-2">
                  This demo simulates the complete flow that happens when a user purchases a product:
                </p>
                <ul className="mb-0">
                  <li>User makes purchase → Stripe processes payment</li>
                  <li>Stripe webhook creates user account in our system</li>
                  <li>Backend generates seamless auto-login token after Stripe proof</li>
                  <li>User is redirected without token in the URL → Frontend handles seamless authentication</li>
                  <li><strong>Result: User is logged in immediately with no Auth0 UI</strong></li>
                </ul>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default SeamlessLoginDemo;
